import { MacroPermission } from '../_models/macro-permissions.enum';

/**
 * Utility functions for managing cookies
 */

/**
 * Retrieves a cookie value by name
 * @param name The name of the cookie to retrieve
 * @returns The cookie value as a string, or null if not found
 */
export function getCookie(name: string): string | null {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
}

/**
 * Sets a cookie with the specified name, value, and options
 * @param name The name of the cookie
 * @param value The value to store
 * @param days Number of days until expiration (optional)
 * @param path Cookie path (default: '/')
 * @param sameSite SameSite attribute (default: 'Strict')
 */
export function setCookie(name: string, value: string, days?: number, path: string = '/', sameSite: string = 'Strict'): void {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + encodeURIComponent(value) + expires + "; path=" + path + "; SameSite=" + sameSite;
}

/**
 * Deletes a cookie by setting its expiration to a past date
 * @param name The name of the cookie to delete
 * @param path Cookie path (default: '/')
 */
export function deleteCookie(name: string, path: string = '/'): void {
  document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=" + path + ";";
}

/**
 * Retrieves and parses the macroPermissions cookie
 * @returns Array of macro permissions, or empty array if not found or invalid
 */
export function getMacroPermissions(): MacroPermission[] {
  try {
    const cookieValue = getCookie('macroPermissions');
    if (cookieValue) {
      const parsed = JSON.parse(cookieValue);
      if (Array.isArray(parsed)) {
        // Filter out invalid permissions and return only valid ones
        return parsed.filter((permission): permission is MacroPermission =>
          typeof permission === 'string' && Object.values(MacroPermission).includes(permission as MacroPermission)
        );
      }
    }
  } catch (error) {
    console.error('Error parsing macroPermissions cookie:', error);
  }
  return [];
}

/**
 * Checks if a specific macro permission exists
 * @param permission The permission to check for
 * @returns true if the permission exists, false otherwise
 */
export function hasMacroPermission(permission: MacroPermission): boolean {
  const permissions = getMacroPermissions();
  return permissions.includes(permission);
}

/**
 * Sets the macroPermissions cookie
 * @param permissions Array of macro permissions
 * @param days Number of days until expiration (default: 7)
 */
export function setMacroPermissions(permissions: MacroPermission[], days: number = 7): void {
  if (Array.isArray(permissions)) {
    const permissionsJson = JSON.stringify(permissions);
    setCookie('macroPermissions', permissionsJson, days);
  } else {
    console.error('Invalid permissions array provided');
  }
}

/**
 * Clears the macroPermissions cookie
 */
export function clearMacroPermissions(): void {
  deleteCookie('macroPermissions');
}